import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { Chat } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { LinkContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat';
import { ActivityIndicator } from '@trinity/chat-components/components/presentation/ActivityIndicator';
import { Chip } from '@trinity/chat-components/components/presentation/Chip';
import { SearchInput } from '@trinity/chat-components/components/presentation/Input.Search';
import { Constants } from '@trinity/chat-components/components/Theme';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useIsFullPageChat } from '../Chat/hooks/useIsFullPageChat';
import {
  FetchMoreHistoryContext,
  ChatHistoryFilterContext,
  ChatHistoryManagerContext,
  ChatHistoryItemsContext,
} from './context';
import { generateHistoryBuckets } from './history';
import { ChatHistoryItemContainer } from './Item.Container';
import { NoChatHistory } from './NoChatHistory';
import { ViewMoreHistory } from './ViewMore';

type Props = {
  className?: string;
  items: Chat.Instance[];
  shared: Chat.Instance[];
};

export const ChatHistory = ({ className, items, shared }: Props) => {
  const historyManager = useContext(ChatHistoryManagerContext);
  const fetchMoreHistory = useContext(FetchMoreHistoryContext);
  // Exclude shared items from time-based buckets (they have their own section)
  const buckets = useMemo(() => generateHistoryBuckets(items, []), [items]);
  const { filter, name, setFilter, setName } = useContext(ChatHistoryFilterContext);

  // Coordinate menu state across all history items
  const [openMenuItemId, setOpenMenuItemId] = useState<string | null>(null);

  const handleMenuOpen = useCallback((itemId: string) => {
    setOpenMenuItemId(itemId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setOpenMenuItemId(null);
  }, []);

  const switcher = useHistorySwitcher();
  const isFullpageChat = useIsFullPageChat();
  const chat = useContext(ActiveChatSessionContext);

  // Filter shared items locally when a search name is active
  const visibleShared = useMemo(() => {
    const safeShared = shared ?? [];
    if (!name?.trim()) return safeShared;
    const query = name.trim().toLowerCase();
    return safeShared.filter((item) => item.name?.toLowerCase().includes(query));
  }, [shared, name]);

  const showSearch = useMemo(() => {
    return !(filter === 'all' && name?.trim?.()?.length === 0);
  }, [filter, name]);

  // "Shared With You" is always shown (when items exist), independent of search/filter mode
  const showSharedSection = !isFullpageChat && visibleShared.length > 0;

  const view = useMemo(
    () => ({
      switcher,
      empty: !buckets.length && !historyManager.loading && !visibleShared.length,
      viewmore: !historyManager.loading && historyManager.hasMore,
      loader: historyManager.loading,
    }),
    [buckets, historyManager.loading, historyManager.hasMore, switcher, visibleShared.length]
  );

  return (
    <Root className={className}>
      {!isFullpageChat && <HistoryLabel>History</HistoryLabel>}

      <Wrap>
        <div style={{ border: '1px solid var(--gray)', borderRadius: 8, marginBottom: 8 }}>
          <SearchInput
            clearable={name.length > 0}
            placeholder="Search chats..."
            onChange={(val) => setName(val)}
            value={name}
          />
        </div>

        <FilterButtons>
          <ChipStyled selected={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </ChipStyled>
          <ChipStyled selected={filter === 'bookmarked'} onClick={() => setFilter('bookmarked')}>
            Pinned
          </ChipStyled>
          <ChipStyled selected={filter === 'profile'} onClick={() => setFilter('profile')}>
            {(() => {
              if (!chat?.context) return 'Current Context';
              switch (chat.context.type) {
                case ChatContextType.Document:
                  return 'Document';
                case ChatContextType.Folder:
                  return 'Folder';
                case ChatContextType.Transcript:
                  return 'Transcript';
                case ChatContextType.Global:
                  return 'Everything';
                case ChatContextType.KolProfile:
                  return 'Profile';
                case ChatContextType.Agent:
                  return chat.context.data.name;
                default:
                  return '';
              }
            })()}
          </ChipStyled>
        </FilterButtons>
      </Wrap>

      {/* Shared With You — always visible (above search results and date buckets) */}
      {showSharedSection && (
        <Bucket>
          <SharedWithYouTitle>Shared With You</SharedWithYouTitle>
          {visibleShared.map((item) => (
            <ChatHistoryItemContainer
              key={item.identifier}
              item={item}
              isMenuOpen={openMenuItemId === item.identifier}
              onMenuOpen={() => handleMenuOpen(item.identifier)}
              onMenuClose={handleMenuClose}
            />
          ))}
        </Bucket>
      )}

      <ChatHistorySearchItems
        visible={showSearch}
        openMenuItemId={openMenuItemId}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
      />

      {view.empty && <NoChatHistory />}
      {!showSearch &&
        buckets.map(({ bucket, items: bucketItems }) => (
          <Bucket key={bucket}>
            {!isFullpageChat && <BucketTitle>{bucket}</BucketTitle>}
            {bucketItems.map((item) => (
              <ChatHistoryItemContainer
                key={item.identifier}
                item={item}
                isMenuOpen={openMenuItemId === item.identifier}
                onMenuOpen={() => handleMenuOpen(item.identifier)}
                onMenuClose={handleMenuClose}
              />
            ))}
          </Bucket>
        ))}
      {!showSearch && view.viewmore && (
        <ViewMoreHistory isLoading={historyManager.loading} onClick={fetchMoreHistory} />
      )}
      {!showSearch && view.loader && (
        <LoadingWrap>
          <ActivityIndicator />
        </LoadingWrap>
      )}
    </Root>
  );
};

function useHistorySwitcher() {
  const chat = useContext(ActiveChatSessionContext);
  return useMemo(() => {
    const contextType = chat?.context?.type;

    return ![ChatContextType.Global].includes(contextType);
  }, [chat?.context?.type]);
}

const Root = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Bucket = styled.div`
  margin-bottom: 20px;
`;

const BucketTitle = styled('div')(({ theme }) => ({
  marginBottom: 5,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.dark1 ?? theme.palette.gray.main,
  whiteSpace: 'nowrap',
  fontSize: 13,
  padding: `0 ${Constants.Sidebar.HorizontalPadding}px`,
}));

const SharedWithYouTitle = styled('div')(({ theme }) => ({
  marginBottom: 5,
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.dark1 ?? theme.palette.gray.main,
  whiteSpace: 'nowrap',
  fontSize: 13,
  padding: `0 ${Constants.Sidebar.HorizontalPadding}px`,
}));

const HistoryLabel = styled.h1`
  color: ${(props) => props.theme.palette.text.primary};
  font-size: 22px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  line-height: 1.2;
  padding: 0 ${Constants.Sidebar.HorizontalPadding}px;
  margin: 0 0 10px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  height: 35px;
  box-sizing: border-box;
  margin: 0 ${Constants.Sidebar.ItemHorizontalPadding}px;
`;

const Wrap = styled.div`
  margin: 15px;
`;

const FilterButtons = styled.div(() => ({
  display: 'flex',
  gap: 8,
}));

const ChipStyled = styled(Chip)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 60,
}));

type ChatHistorySearchItemsProps = {
  visible: boolean;
  openMenuItemId: string | null;
  onMenuOpen: (itemId: string) => void;
  onMenuClose: () => void;
};

const ChatHistorySearchItems = (props: ChatHistorySearchItemsProps) => {
  const historyItems = useContext(ChatHistoryItemsContext);
  const chat = useContext(ActiveChatSessionContext);
  const activeBrandIdentifier = useActiveBrandIdentifier();
  const link = useContext(LinkContext);
  const { filter, name, setFilter: _setFilter, setName: _setName } = useContext(ChatHistoryFilterContext);

  const getIncludeAdditional = useCallback(() => {
    if (
      !link?.initialized &&
      link?.chatIdentifier &&
      !historyItems.map((i) => i.identifier).includes(link.chatIdentifier)
    ) {
      return [link.chatIdentifier];
    }
    return null;
  }, [historyItems, link?.initialized, link?.chatIdentifier]);

  const queryKey = useMemo(() => {
    const mode: 'page' | 'all' = filter === 'profile' ? 'page' : 'all';
    return {
      mode,
      cursor: null,
      pageSize: 20,
      includeAdditional: getIncludeAdditional(),
      context: chat.context,
      brandIdentifier: activeBrandIdentifier,
      name: name.trim(),
      isBookmarked: filter === 'bookmarked',
    };
  }, [getIncludeAdditional, chat.context, activeBrandIdentifier, name, filter]);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: [`chat:get:history`, queryKey] as const,
    queryFn: ({ queryKey }) => {
      return apiClient.chat.searchChatHistory({
        mode: queryKey[1].mode,
        cursor: queryKey[1].cursor,
        pageSize: queryKey[1].pageSize,
        includeAdditional: queryKey[1].includeAdditional,
        context: queryKey[1].context,
        brandIdentifier: queryKey[1].brandIdentifier,
        name: queryKey[1].name,
        isBookmarked: queryKey[1].isBookmarked,
      });
    },
    enabled: props.visible,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  if (!props.visible) {
    return null;
  }

  return (
    <>
      {query.data?.items.map((item) => (
        <ChatHistoryItemContainer
          key={item.identifier}
          item={item}
          isMenuOpen={props.openMenuItemId === item.identifier}
          onMenuOpen={() => props.onMenuOpen(item.identifier)}
          onMenuClose={props.onMenuClose}
        />
      ))}
    </>
  );
};
